import { Footer } from '@/app/components/Footer'
import { InitialResults } from 'instantsearch.js'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'
import { CollectionSearchParameters } from 'typesense-instantsearch-adapter'
import { getSearchClient } from '../../search/lib/search-client'
import { Header } from '../../shared/components/Header'
import { Newsletter } from '../../shared/components/Newsletter'
import { TopProvidersSection } from '../../shared/components/TopProvidersSection'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { CoursesHeader } from '../components'

import { CategoriesShowcase } from '../components/CategoriesShowcase'
import { SubCategoryCoursesHits } from '../components/SubCategoryCoursesHits'
import { SearchInputProvider } from '../contexts/InputSearchContext'

type ExternalCoursesPageProps = {
  csrSearchConfig: CollectionSearchParameters
  initialResults?: InitialResults[]
}

export function ExternalCoursesPage({
  csrSearchConfig,
  initialResults,
}: ExternalCoursesPageProps) {
  const [subcategoriesCoursesInitialResults, categoriesInitialResults] =
    initialResults ?? []
  const t = useTranslations('Courses')
  const { currentLocale } = useLanguageSelection()
  const router = useRouter()

  const onSearchHandler = async (term: string) => {
    await router.push({
      pathname: '/search',
      query: { 'courses[query]': term },
    })
  }

  const client = useMemo(
    () =>
      getSearchClient('', {
        collectionSpecificSearchParameters: csrSearchConfig,
      }),
    [csrSearchConfig]
  )

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <SearchInputProvider>
        <Header className="z-40" />
        <CoursesHeader
          className="md:mx-8"
          onSearch={async (searchTerm) => {
            await onSearchHandler(searchTerm)
          }}
        />
        <InstantSearchSSRProvider
          initialResults={subcategoriesCoursesInitialResults}
        >
          <InstantSearch
            indexName="subcategories_courses"
            searchClient={client}
            numberLocale={currentLocale}
            stalledSearchDelay={500}
            routing={false}
            future={{
              preserveSharedStateOnUnmount: true,
            }}
          >
            <div className="container mb-20 mt-12">
              <SubCategoryCoursesHits
                splitGroupsSize={3}
                className="flex flex-col gap-12"
              >
                <InstantSearchSSRProvider
                  initialResults={categoriesInitialResults}
                >
                  <InstantSearch
                    indexName="categories"
                    searchClient={client}
                    numberLocale={currentLocale}
                    stalledSearchDelay={500}
                    routing={false}
                    future={{
                      preserveSharedStateOnUnmount: true,
                    }}
                  >
                    <CategoriesShowcase />
                  </InstantSearch>
                </InstantSearchSSRProvider>
              </SubCategoryCoursesHits>
            </div>
          </InstantSearch>
        </InstantSearchSSRProvider>
        <TopProvidersSection variant="static" />
        <Newsletter />
        <Footer />
      </SearchInputProvider>
    </>
  )
}
